import { makeStyles } from '@fluentui/react-components';
import DOMPurify from 'dompurify';
import React, { useMemo } from 'react';
import { KatexType, useKatex } from '../../hooks/useKatex';

interface AiaLatexProps {
    content: string;
}

type Segment = string | { html: string };

const useClasses = makeStyles({
    // KaTeX positions boxes with inline CSS that TeX from the answer controls (\raisebox, \smash, a
    // negative \kern), and maxSize caps only positive sizes, so clip its output to this box rather than
    // let it draw over the rest of the chat (#2561).
    root: {
        overflowX: 'auto',
        overflowY: 'hidden',
    },
});

export const AiaLatex: React.FC<AiaLatexProps> = ({ content }) => {
    const katex = useKatex();
    const classes = useClasses();
    // Until KaTeX has loaded (it never does if its chunk fails) the content is shown as plain text.
    const segments = useMemo<Segment[]>(() => (katex ? splitLatex(content, katex) : [content]), [content, katex]);

    return (
        <div className={classes.root}>
            {segments.map((segment, index) =>
                typeof segment === 'string' ? (
                    <React.Fragment key={index}>{segment}</React.Fragment>
                ) : (
                    <span key={index} dangerouslySetInnerHTML={{ __html: sanitizeHtml(segment.html) }} />
                ),
            )}
        </div>
    );
};

function sanitizeHtml(html: string | null | undefined) {
    return html
        ? DOMPurify.sanitize(html, {
              ALLOWED_TAGS: [
                  'span',
                  'p',
                  'div',
                  'math',
                  'mrow',
                  'mi',
                  'mn',
                  'mo',
                  'msup',
                  'mfrac',
                  'annotation',
                  'semantics',
                  'mtext',
              ],
              ALLOWED_ATTR: ['class', 'style', 'data-*', 'xmlns', 'encoding', 'stretchy', 'mathvariant', 'aria-*'],
          })
        : '';
}

// Helper function to detect LaTeX
export const containsLatex = (text: string): boolean => {
    return /\\frac|\\cdot|\\times|\\ast|\\div|\\sum|\\prod|\\int|\\%|\\text|\\mathrm|\\mathbf|\\left|\\right|\\begin\{align\}|\$\$|\$|\\\(|\\\[/i.test(
        text,
    );
};

// Common LaTeX expressions. The first branch takes a whole calculation that ends in `= N kr`, but only
// TeX commands, digits and operators, so the prose before it stays text. It is tried at every command
// and digit, so its length cap keeps a long run of them from taking quadratic time.
const latexRegex =
    /((?:\\(?:[a-zA-Z]+(?:\{[^{}$]*\})*|%)|[\d(])(?:\\(?:[a-zA-Z]+(?:\{[^{}$]*\})*|%)|[\d\s.,+\-*/()^_=]){0,200}=\s*\d+\s*kr|\\frac\{[^{}]+\}\{[^{}]+\}|\\sqrt(\[[^\]]*\])?\{[^{}]+\}|\\(sum|prod|int)(_\{[^{}]+\})?\^?\{?[^{}]*\}?|\\(alpha|beta|gamma|delta|theta|lambda|mu|pi|sigma|omega)|\\(times|div|cdot|pm|leq|geq|neq|approx|equiv|infty))/g;

// Text between the matches stays text, so KaTeX output is the only HTML this component renders. `text`
// is a markdown text node, already entity-decoded, so `&lt;div style=…&gt;` in a bot answer arrives here
// as a real tag (#2561).
const splitLatex = (text: string, katex: KatexType): Segment[] => {
    const segments: Segment[] = [];
    let last = 0;
    for (const match of text.matchAll(latexRegex)) {
        segments.push(text.slice(last, match.index), renderTex(match[0], katex));
        last = match.index + match[0].length;
    }
    segments.push(text.slice(last));
    return segments;
};

// Sizes written in the TeX are capped at maxSizeEm, and KaTeX's own offsets around a capped box stay well
// under twice that. maxSize caps only positive sizes, though, so a negative \raisebox or \rule (also via
// \def) still yields a strut millions of em tall that stretches the bubble; clipping can't stop that.
const maxSizeEm = 10;

const hasOversizedLength = (html: string): boolean =>
    [...html.matchAll(/style="([^"]*)"/g)].some(([, style]) =>
        [...style.matchAll(/(-?\d+(?:\.\d+)?)em/g)].some(([, length]) => Math.abs(Number(length)) > 2 * maxSizeEm),
    );

const renderTex = (tex: string, katex: KatexType): Segment => {
    try {
        // In math a comma is punctuation and gets a space after it, so decimals are written with a dot.
        const html = katex.renderToString(tex.replace(/(\d),(\d)/g, '$1.$2'), {
            throwOnError: false,
            displayMode: false,
            maxSize: maxSizeEm,
        });
        return hasOversizedLength(html) ? tex : { html };
    } catch (error) {
        // throwOnError: false only covers ParseError; KaTeX rethrows anything else (e.g. a stack
        // overflow on deeply nested input).
        console.error('LaTeX rendering failed:', error);
        return tex;
    }
};
